import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./ui";

/**
 * Fullscreen image viewer with proper gestures:
 *  • pinch to zoom toward your fingers  • one-finger drag to pan
 *  • double-tap to zoom in/out          • mouse wheel to zoom (desktop)
 *  • ✕ or tap the empty area to close
 * Rendered via a portal to <body> so position:fixed isn't trapped by a
 * `.glass` (backdrop-filter) ancestor.
 */
const MIN = 1, MAX = 6;
type Pt = { clientX: number; clientY: number };
const dist = (a: Pt, b: Pt) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const mid = (a: Pt, b: Pt) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

export default function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [tf, setTf] = useState({ s: 1, x: 0, y: 0 });

  const gesture = useRef<{ type: "pinch" | "pan"; d?: number; s: number; x: number; y: number; sx?: number; sy?: number } | null>(null);
  const moved = useRef(false);
  const lastTap = useRef(0);
  const tfRef = useRef(tf); tfRef.current = tf;

  useEffect(() => {
    const r = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", r);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("resize", r); document.body.style.overflow = prev; };
  }, []);

  const fit = nat ? Math.min(vp.w / nat.w, vp.h / nat.h) : 1;
  const fitW = nat ? nat.w * fit : 0;
  const fitH = nat ? nat.h * fit : 0;

  const centered = useCallback((s = 1) => ({ s, x: (vp.w - fitW * s) / 2, y: (vp.h - fitH * s) / 2 }), [vp, fitW, fitH]);
  useEffect(() => { if (nat) setTf(centered(1)); }, [nat, vp.w, vp.h]); // eslint-disable-line react-hooks/exhaustive-deps

  const clamp = useCallback((s: number, x: number, y: number) => {
    const w = fitW * s, h = fitH * s;
    const nx = w <= vp.w ? (vp.w - w) / 2 : Math.min(0, Math.max(vp.w - w, x));
    const ny = h <= vp.h ? (vp.h - h) / 2 : Math.min(0, Math.max(vp.h - h, y));
    return { s, x: nx, y: ny };
  }, [fitW, fitH, vp]);

  const zoomAt = (fx: number, fy: number, ns: number) => {
    const p = tfRef.current;
    const s1 = Math.min(MAX, Math.max(MIN, ns));
    setTf(clamp(s1, fx - (fx - p.x) * (s1 / p.s), fy - (fy - p.y) * (s1 / p.s)));
  };

  const onTouchStart = (e: React.TouchEvent) => {
    moved.current = false;
    const p = tfRef.current;
    if (e.touches.length === 2) {
      gesture.current = { type: "pinch", d: dist(e.touches[0], e.touches[1]), s: p.s, x: p.x, y: p.y };
    } else if (e.touches.length === 1) {
      gesture.current = { type: "pan", s: p.s, x: e.touches[0].clientX - p.x, y: e.touches[0].clientY - p.y, sx: e.touches[0].clientX, sy: e.touches[0].clientY };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const g = gesture.current; if (!g) return;
    if (g.type === "pinch" && e.touches.length >= 2) {
      const nd = dist(e.touches[0], e.touches[1]);
      const m = mid(e.touches[0], e.touches[1]);
      const s1 = Math.min(MAX, Math.max(MIN, g.s * (nd / (g.d || nd))));
      moved.current = true;
      setTf(clamp(s1, m.x - (m.x - g.x) * (s1 / g.s), m.y - (m.y - g.y) * (s1 / g.s)));
    } else if (g.type === "pan" && e.touches.length === 1) {
      const cx = e.touches[0].clientX, cy = e.touches[0].clientY;
      if (Math.hypot(cx - (g.sx ?? cx), cy - (g.sy ?? cy)) > 6) moved.current = true;
      setTf(clamp(g.s, cx - g.x, cy - g.y));
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const wasPinch = gesture.current?.type === "pinch";
    gesture.current = null;
    if (e.touches.length > 0 || moved.current || wasPinch) return;
    const t = e.changedTouches[0];
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      if (tfRef.current.s > 1.05) setTf(centered(1)); else zoomAt(t.clientX, t.clientY, 2.5);
    } else {
      lastTap.current = now;
      const sNow = tfRef.current.s;
      setTimeout(() => { if (lastTap.current === now && sNow <= 1.05) onClose(); }, 280);
    }
  };

  return createPortal(
    <div
      onClick={() => { if (!moved.current && tfRef.current.s <= 1.05) onClose(); }}
      onWheel={(e) => zoomAt(e.clientX, e.clientY, tfRef.current.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15))}
      onDoubleClick={(e) => { if (tfRef.current.s > 1.05) setTf(centered(1)); else zoomAt(e.clientX, e.clientY, 2.5); }}
      style={{
        position: "fixed", inset: 0, zIndex: 4000, background: "rgba(0,0,0,0.95)",
        overflow: "hidden", touchAction: "none", userSelect: "none",
      }}
    >
      {nat && (
        <img
          src={src}
          alt=""
          draggable={false}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            position: "absolute", left: 0, top: 0, width: fitW, height: fitH,
            transform: `translate3d(${tf.x}px, ${tf.y}px, 0) scale(${tf.s})`,
            transformOrigin: "0 0", touchAction: "none", willChange: "transform",
            transition: gesture.current ? "none" : "transform 0.12s ease-out",
          }}
        />
      )}
      {/* hidden loader to read natural size before showing */}
      {!nat && (
        <img src={src} alt="" onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none", maxWidth: "100%", maxHeight: "100%" }} />
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        style={{
          position: "fixed", top: 14, right: 14, zIndex: 4001,
          width: 42, height: 42, borderRadius: 12, border: "none", cursor: "pointer",
          background: "rgba(255,255,255,0.16)", color: "#fff", display: "grid", placeItems: "center", backdropFilter: "blur(6px)",
        }}
      >
        <Icon name="x" size={22} />
      </button>
    </div>,
    document.body,
  );
}
